/**
 * Deposit Service - SOP #3
 * Handles deposit verification API calls
 */

import api from "../../../shared/api";
import { logError } from "../../../shared/utils/logger.js";

/**
 * Verify deposit and generate receipt
 * @param {string} quoteId - Quote ID
 * @param {Object} depositData - Deposit verification data
 * @param {string} depositData.adminId - Admin ID (required)
 * @param {string} depositData.paymentReference - Payment reference (required, min 3 chars)
 * @param {number} depositData.depositAmount - Deposit amount received (required)
 * @param {string} [depositData.verificationNotes] - Verification notes
 * @returns {Promise<Object>} Result with quote, order, receipt, and templates
 */
export const verifyDeposit = async (quoteId, depositData) => {
  try {
    const response = await api.post(
      `/quotes/deposit/${quoteId}/verify-deposit`,
      {
        adminId: depositData.adminId,
        paymentReference: depositData.paymentReference,
        depositAmount: depositData.depositAmount,
        verificationNotes: depositData.verificationNotes || "",
      }
    );

    // API client returns parsed JSON directly
    // Response can be either:
    // 1. { success: true, data: {...}, message: "..." } (full response)
    // 2. { order: {...}, quote: {...}, receipt: {...}, templates: {...} } (just data)

    // Check if response has success property (full response structure)
    if (response.success !== undefined) {
      if (!response.success) {
        throw new Error(response.error || "Failed to verify deposit");
      }
      return {
        success: true,
        data: response.data,
        message: response.message || "Deposit verified successfully",
      };
    }

    // If no success property, assume it's the data object directly
    // Check if it has the expected structure (order, quote, receipt, templates)
    if (
      response.order ||
      response.quote ||
      response.receipt ||
      response.templates
    ) {
      return {
        success: true,
        data: response,
        message: "Deposit verified successfully",
      };
    }

    // If structure doesn't match, treat as error
    throw new Error("Unexpected response format");
  } catch (error) {
    logError("Error verifying deposit", {
      quoteId,
      depositData,
      error: error.message,
      status: error.response?.status,
    });
    return {
      success: false,
      error:
        error.response?.data?.error ||
        error.message ||
        "Failed to verify deposit",
    };
  }
};

/**
 * Upload admin POP (image/PDF) and get field suggestions.
 */
export const uploadDepositPop = async (quoteId, files, options = {}) => {
  try {
    const formData = new FormData();
    (files || []).forEach((file) => {
      formData.append("popFiles", file);
    });
    if (options.adminId) formData.append("adminId", options.adminId);
    if (options.notes) formData.append("notes", options.notes);

    const response = await api.post(
      `/quotes/deposit/${quoteId}/pop`,
      formData
    );

    if (response.success === false) {
      throw new Error(response.error || "Failed to upload POP");
    }

    return {
      success: true,
      data: response.data || response,
      message: response.message || "POP uploaded",
    };
  } catch (error) {
    logError("Error uploading deposit POP", {
      quoteId,
      error: error.message,
      status: error.response?.status,
    });
    return {
      success: false,
      error:
        error.response?.data?.error ||
        error.message ||
        "Failed to upload POP",
    };
  }
};

/**
 * Re-extract POP field suggestions for the latest stored POP.
 */
export const extractDepositPop = async (quoteId) => {
  try {
    const response = await api.post(
      `/quotes/deposit/${quoteId}/pop/extract`,
      {}
    );
    if (response.success === false) {
      throw new Error(response.error || "Failed to extract POP fields");
    }
    return {
      success: true,
      data: response.data || response,
    };
  } catch (error) {
    logError("Error extracting deposit POP", {
      quoteId,
      error: error.message,
      status: error.response?.status,
    });
    return {
      success: false,
      error:
        error.response?.data?.error ||
        error.message ||
        "Failed to extract POP fields",
    };
  }
};

/**
 * Get receipt PDF for download
 * @param {string} quoteId - Quote ID
 * @returns {Promise<Blob>} Receipt PDF blob
 */
export const getDepositReceipt = async (quoteId) => {
  try {
    // Use the binary endpoint for PDF download
    const response = await api.binary(
      `/quotes/deposit/${quoteId}/receipt`,
      {},
      "application/pdf"
    );

    // api.binary returns a Response object, check if ok
    if (!response.ok) {
      // Try to parse error JSON
      try {
        const errorData = await response.json();
        throw new Error(
          errorData.error || `Failed to download receipt: ${response.status}`
        );
      } catch (parseError) {
        throw new Error(
          `Failed to download receipt: ${response.status} ${response.statusText}`
        );
      }
    }

    // Return blob from response
    return await response.blob();
  } catch (error) {
    logError("Error getting receipt", {
      quoteId,
      error: error.message,
      status: error.response?.status,
    });
    throw error; // Re-throw to let caller handle
  }
};

/**
 * Get receipt and templates for verified deposit
 * @param {string} quoteId - Quote ID
 * @returns {Promise<Object>} Receipt and templates data
 */
export const getReceiptAndTemplates = async (quoteId) => {
  try {
    const response = await api.get(
      `/quotes/deposit/${quoteId}/receipt-and-templates`
    );

    // Handle both response structures
    if (response.success !== undefined) {
      if (!response.success) {
        throw new Error(
          response.error || "Failed to get receipt and templates"
        );
      }
      return {
        success: true,
        data: response.data,
      };
    }

    // If response is the data object directly
    if (response.receipt || response.templates) {
      return {
        success: true,
        data: response,
      };
    }

    throw new Error("Unexpected response format");
  } catch (error) {
    logError("Error getting receipt and templates", {
      quoteId,
      error: error.message,
      status: error.response?.status,
    });
    return {
      success: false,
      error:
        error.response?.data?.error ||
        error.message ||
        "Failed to get receipt and templates",
    };
  }
};
